// NSARA Stores V2 — création de boutique en 2 champs (section 6 de la mission).
//
// Volontairement minimal: Nom (obligatoire) + Description courte (facultatif).
// Aucun logo, téléphone, adresse, email, couleur ni catégorie n'est demandé —
// le backend (backend/routes/shops.js) les traite déjà comme optionnels.
// Conséquence directe de la règle Cloudinary (section 28): la création d'une
// boutique ne dépend JAMAIS d'un téléversement d'image, donc elle ne peut pas
// être bloquée par le problème Cloudinary en cours.
import React, { useState } from 'react';
import { Loader2, Store } from 'lucide-react';
import { createShop } from '../../services/apiClient';
import Modal from './Modal';

const EMPTY = { name: '', description: '' };

export default function CreateShopDialog({ open, onClose, onCreated, businessId }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setForm(EMPTY); setError(''); setSaving(false); };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose?.();
  };

  const submit = async (event) => {
    event.preventDefault();
    const name = form.name.trim();
    if (!name || saving || !businessId) return;
    setSaving(true);
    setError('');
    try {
      const { shop } = await createShop({ businessId, name, description: form.description.trim() });
      reset();
      onCreated?.(shop);
    } catch (err) {
      setError(err?.message || 'Création impossible. Réessayez.');
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} labelledBy="create-shop-title" size="sm">
      <form className="stores-dialog" onSubmit={submit} noValidate>
        <div className="stores-dialog-head">
          <span className="stores-dialog-icon" aria-hidden="true"><Store size={18} /></span>
          <div>
            <h2 id="create-shop-title" className="stores-dialog-title">Créer une boutique</h2>
            <p id="create-shop-help" className="stores-dialog-subtitle">
              Vous pourrez ajouter le logo, les produits et les réglages ensuite.
            </p>
          </div>
        </div>

        <div className="stores-field">
          <label className="stores-label" htmlFor="shop-name">Nom de la boutique *</label>
          <input
            id="shop-name"
            name="name"
            className="stores-input"
            value={form.name}
            maxLength={200}
            autoComplete="off"
            placeholder="Ex. First Class"
            aria-required="true"
            aria-invalid={error ? undefined : undefined}
            disabled={saving}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </div>

        <div className="stores-field">
          <label className="stores-label" htmlFor="shop-description">Description courte (facultatif)</label>
          <textarea
            id="shop-description"
            name="description"
            className="stores-input stores-textarea"
            value={form.description}
            maxLength={500}
            rows={2}
            placeholder="Ex. Mode masculine"
            disabled={saving}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
        </div>

        {error && <p className="stores-alert stores-alert--error" role="alert">{error}</p>}

        <div className="stores-dialog-actions">
          <button type="button" className="stores-btn stores-btn--ghost" onClick={handleClose} disabled={saving}>
            Annuler
          </button>
          <button type="submit" className="stores-btn stores-btn--primary" disabled={saving || !form.name.trim()}>
            {saving && <Loader2 size={15} aria-hidden="true" className="stores-spin" />}
            {saving ? 'Création…' : 'Créer la boutique'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
